import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  Claim,
  ClaimPostingDetails,
  ClaimPostingRecord,
  UpdateClaimThunkParams,
  ClaimStatus,
  Attachment,
  Note,
} from '@dataTypes/claimFeature.types'
import claimsData from '@assets/mockdata/Claims/claimFeatureData'
import {
  trackingData,
  TrackingRecord,
} from '@assets/mockdata/Claims/trackingData'
import { logger } from '@utils/safeLogger'
import { claimPostingData } from '@assets/mockdata/Claims/claimPostingData'
import { claimPostingDetails } from '@assets/mockdata/Claims/claimPostingDetails'

let mockClaims: Claim[] = [...claimsData]
const mockTrackingRecords: TrackingRecord[] = [...trackingData]
const mockClaimPostings: ClaimPostingRecord[] = [...claimPostingData]
const mockClaimPostingDetails: ClaimPostingDetails = {
  ...claimPostingDetails,
  claimStatus:
    claimPostingDetails.claimStatus as ClaimPostingDetails['claimStatus'],
  checkStatus:
    claimPostingDetails.checkStatus as ClaimPostingDetails['checkStatus'],
}

export const fetchAllFeatureClaims = createAsyncThunk<Claim[], void>(
  'api/claims/fetch/all',
  async (_, { rejectWithValue }) => {
    try {
      return mockClaims
    } catch (error) {
      return rejectWithValue('Failed to fetch claims')
    }
  },
)

export const fetchAllTrackingRecords = createAsyncThunk<TrackingRecord[], void>(
  'api/tracking/fetch/all',
  async (_, { rejectWithValue }) => {
    try {
      return mockTrackingRecords
    } catch (error) {
      return rejectWithValue('Failed to fetch tracking records')
    }
  },
)

export const fetchClaimPostings = createAsyncThunk<ClaimPostingRecord[], void>(
  'api/claims/fetch/ClaimPostings',
  async (_, { rejectWithValue }) => {
    try {
      return mockClaimPostings
    } catch (error) {
      return rejectWithValue('Failed to fetch claim postings')
    }
  },
)

export const fetchClaimPostingDetails = createAsyncThunk<
  ClaimPostingDetails,
  void
>('api/claims/fetch/ClaimPostingDetails', async (_, { rejectWithValue }) => {
  try {
    return mockClaimPostingDetails
  } catch (error) {
    return rejectWithValue('Failed to fetch claim posting details')
  }
})

export const addFeatureClaim = createAsyncThunk<Claim, Claim>(
  'api/claims/add/claim',
  async (newClaim, { rejectWithValue }) => {
    try {
      mockClaims = [...mockClaims, newClaim]
      return newClaim
    } catch (error) {
      return rejectWithValue('Failed to add claim')
    }
  },
)

export const updateFeatureClaim = createAsyncThunk<
  Claim,
  UpdateClaimThunkParams
>(
  'api/claims/update/claim',
  async ({ id, body }: UpdateClaimThunkParams, { rejectWithValue }) => {
    try {
      mockClaims = mockClaims.map((claim) =>
        claim.id === id ? { ...claim, ...body } : claim,
      )

      const updatedClaim = mockClaims.find((claim) => claim.id === id)
      if (!updatedClaim) {
        logger.error('Claim not found')
        throw new Error(`Claim with ID ${id} not found`)
      }

      return updatedClaim
    } catch (error) {
      logger.error('Update Error', error)
      return rejectWithValue('Failed to update claim')
    }
  },
)

export const removeFeatureClaim = createAsyncThunk<string, string>(
  'api/claims/remove/claim',
  async (id, { rejectWithValue }) => {
    try {
      mockClaims = mockClaims.filter((claim) => claim.id !== id)
      return id
    } catch (error) {
      return rejectWithValue('Failed to remove claim')
    }
  },
)

export const updateClaimStatus = createAsyncThunk<
  Claim,
  { claimId: string; newStatus: keyof typeof ClaimStatus }
>(
  'api/claims/update/status',
  async ({ claimId, newStatus }, { rejectWithValue }) => {
    try {
      mockClaims = mockClaims.map((claim) =>
        claim.id === claimId
          ? {
              ...claim,
              status: ClaimStatus[newStatus as keyof typeof ClaimStatus],
            }
          : claim,
      )

      const updatedClaim = mockClaims.find((claim) => claim.id === claimId)
      if (!updatedClaim) {
        logger.error('Claim not found')
        throw new Error(`Claim with ID ${claimId} not found`)
      }

      return updatedClaim
    } catch (error) {
      logger.error('Update Error', error)
      return rejectWithValue('Failed to update claim status')
    }
  },
)

export const addTrackingRecord = createAsyncThunk<
  TrackingRecord,
  TrackingRecord
>('api/tracking/add/record', async (newRecord, { rejectWithValue }) => {
  try {
    const requiredFields = [
      'id',
      'checkEFTNumber',
      'insuranceCarrier',
      'modeOfPayment',
      'amount',
    ]
    const missingFields = requiredFields.filter(
      (field) => !(field in newRecord),
    )

    if (missingFields.length > 0) {
      return rejectWithValue(
        `Missing Required Fields: ${missingFields.join(', ')}`,
      )
    }

    if (!Array.isArray(mockTrackingRecords)) {
      return rejectWithValue('Tracking records data is corrupted')
    }

    return newRecord
  } catch (error) {
    return rejectWithValue('Failed to add tracking record')
  }
})

export const addOrUpdateNoteThunk = createAsyncThunk<
  Claim,
  { claimId: string; notes: Note[] }
>(
  'api/claims/addOrUpdate/note',
  async ({ claimId, notes }, { rejectWithValue }) => {
    try {
      mockClaims = mockClaims.map((claim) =>
        claim.id === claimId
          ? {
              ...claim,
              attachments: claim.attachments.map((attachment) => ({
                ...attachment,
                notes: notes.filter((note) =>
                  attachment.notes.some((n) => n.noteId === note.noteId),
                ),
              })),
            }
          : claim,
      )

      const updatedClaim = mockClaims.find((claim) => claim.id === claimId)
      if (!updatedClaim) throw new Error(`Claim with ID ${claimId} not found`)

      return updatedClaim
    } catch (error) {
      logger.error('Error updating notes', error)
      return rejectWithValue('Failed to update notes')
    }
  },
)

export const addOrUpdateAttachmentThunk = createAsyncThunk<
  Claim,
  { claimId: string; attachments: Attachment[] }
>(
  'api/claims/addOrUpdate/attachment',
  async ({ claimId, attachments }, { rejectWithValue }) => {
    try {
      mockClaims = mockClaims.map((claim) =>
        claim.id === claimId ? { ...claim, attachments } : claim,
      )

      const updatedClaim = mockClaims.find((claim) => claim.id === claimId)
      if (!updatedClaim) throw new Error(`Claim with ID ${claimId} not found`)

      return updatedClaim
    } catch (error) {
      logger.error('Error updating attachments', error)
      return rejectWithValue('Failed to update attachments')
    }
  },
)
